import {Badge} from "../../../basic/badge.tsx";
import {Calendar} from "lucide-react";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {ColumnDef, DataTable} from "../../../shared/tables/DataTable.tsx";
import {VwpUser} from "../../../../store/generated/api.ts";
import {getDisplayName, getInitials} from "../../../../utils/nameUtils.ts";
import {formatDate} from "../../../../utils/dateUtils";
import {getTeamDisplayName} from "../../../../utils/teamUtils";
import LicenseChip from "../../../shared/selectors/LicenseChip";

interface UserTableProps {
    users: VwpUser[];
    onUserClick: (user: VwpUser) => void;
}

const isOnline = (loginDate: string | null): boolean => {
    if (!loginDate) return false;
    const login = new Date(loginDate);
    const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000);
    return login > fifteenMinutesAgo;
};

export function UserTable({users, onUserClick}: UserTableProps) {
    const {t} = useTranslationWithKeys();

    const columns: ColumnDef<VwpUser>[] = [
        {
            key: "firstName",
            header: t('common.table.name'),
            cell: (user) => (
                <div className="flex items-center gap-3">
                    <div
                        className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-sm font-medium">
                        {getInitials(user)}
                    </div>
                    <div>
                        <p className="font-medium">{getDisplayName(user)}</p>
                        <p className="text-muted-foreground text-sm">{user.email}</p>
                    </div>
                </div>
            )
        },
        {
            key: "team",
            header: t('common.table.team'),
            cell: (user) =>
                getTeamDisplayName({
                    teamName: user.team?.name,
                    firstName: user.firstName,
                    lastName: user.lastName
                }) || t('common.noTeam')
        },
        {
            key: "role",
            header: t('common.table.role'),
            cell: (user) => (
                <Badge variant={user.role === "ADMIN" ? "default" : "outline"}>
                    {t(`admin.users.roles.${user.role.toLowerCase()}`)}
                </Badge>
            )
        },
        {
            key: "licenses",
            header: t('common.table.licenses'),
            cell: (user) => (
                user.licenses && user.licenses.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                        {user.licenses.map((license, idx) => (
                            <LicenseChip
                                key={idx}
                                productName={license.product}
                                licenseId={license.id}
                                variant="compact"
                            />
                        ))}
                    </div>
                ) : (
                    <span className="text-muted-foreground">{t('common.none')}</span>
                )
            )
        },
        {
            key: "joinedDate",
            header: t('common.table.joined'),
            cell: (user) => (
                <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground"/>
                    {formatDate(user.joinedDate)}
                </div>
            )
        },
        {
            key: "lastPlatformLogin",
            header: t('common.table.lastPlatformLogin'),
            cell: (user) => {
                const online = isOnline(user.lastPlatformLogin);

                return (
                    <div className="flex items-center gap-2">
                        {online && (
                            <span className="h-2 w-2 rounded-full bg-green-500"/>
                        )}
                        <span>
                            {user.lastPlatformLogin
                                ? formatDate(user.lastPlatformLogin)
                                : t('common.never')}
                        </span>
                    </div>
                );
            }
        },
        {
            key: "lastLicenseLogin",
            header: t('common.table.lastLicenseLogin'),
            cell: (user) => (
                <span>
                    {user.lastLicenseLogin
                        ? formatDate(user.lastLicenseLogin)
                        : t('common.never')}
                </span>
            )
        },
        {
            key: "status",
            header: t('common.table.status'),
            cell: (user) => (
                <Badge variant={user.status === "ACTIVE" ? "default" : "secondary"}>
                    {t(`admin.users.status.${user.status.toLowerCase()}`)}
                </Badge>
            )
        }
    ];

    return (
        <DataTable
            data={users}
            columns={columns}
            onRowClick={onUserClick}
            withBorder={false}
        />
    );
}